import { useMemoizedFn } from 'ahooks';
import { useTranslateReport } from 'hooks/useTranslateReport';
import { LocalMessage } from '../conversation';
import { modeTranslate, overseaTranslate, TranslateMode } from 'services/api/overseaTranslate';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { useSystemLanguageValue } from '../system/useSystem';
import { useBatchUpdateMessages } from '../message';
import { getJolectAiGuideMarkdown } from 'views/Conversation/Chat/utils';

/** 可翻译消息的内容字段 */
export const getTranslationContentKey = (messageType: Conversation.MessageType) => {
  if (messageType === 'ATTACHMENT') {
    return 'attachmentContent' as const;
  }
  if (messageType === 'JL_FORM') {
    return 'jlFormContent' as const;
  }
  if (messageType === 'JL_FIXED_REPLY') {
    return 'jlFixedReplyContent' as const;
  }
  if (messageType === 'JL_AI_GUIDE') {
    return 'jlAiGuideContent' as const;
  }
  return 'textContent' as const;
};

export const useSingleOrBatchTrans = () => {
  const sysLang = useSystemLanguageValue();
  const batchUpdateMessages = useBatchUpdateMessages();
  const translateReport = useTranslateReport();

  const singleOrBatchTrans = useMemoizedFn(
    async (
      msg: LocalMessage | LocalMessage[],
      _lang?: General.Language,
      mode?: TranslateMode,
      translated?: boolean,
    ) => {
      const lang = _lang || sysLang;
      const languageKey = sysLangToTranslationLang(lang);

      let source: Conversation.Translation['source'] = 'chatpp';

      const isList = Array.isArray(msg);
      if (!isList) {
        const { messageType } = msg;
        if (messageType === 'ATTACHMENT') {
          source = msg.attachmentContent?.autoTranslation?.source ? 'sender' : 'chatpp';
        } else {
          source = msg.textContent?.autoTranslation?.source ? 'sender' : 'chatpp';
        }
      }

      const msgs = (isList ? msg : [msg]).filter((msg) => {
        const content = msg[getTranslationContentKey(msg.messageType)];
        return !['pending'].includes(content?.translationContent?.[languageKey]?.status as string);
      });

      if (msgs.length === 0) {
        return;
      }

      batchUpdateMessages(
        msgs.map((_msg) => {
          return [
            _msg.msgId,
            (msg) => {
              if (!msg) {
                return;
              }
              const contentKey = getTranslationContentKey(msg.messageType);
              const translationContent = {
                ...msg[contentKey]?.translationContent,
                [languageKey]: {
                  language: languageKey,
                  status: 'pending',
                  text: '',
                  translated,
                },
              };

              return {
                ...msg,
                hideTranslate: false,
                [contentKey]: {
                  ...(msg[contentKey] as any),
                  translationContent,
                },
              };
            },
          ];
        }),
      );

      const texts = msgs.map((msg) => {
        if (msg.messageType === 'ATTACHMENT') {
          return msg.attachmentContent?.text || '';
        }
        if (msg.messageType === 'JL_FORM') {
          return msg.jlFormContent?.text || '';
        }
        if (msg.messageType === 'JL_FIXED_REPLY') {
          return msg.jlFixedReplyContent?.text || '';
        }
        if (msg.messageType === 'JL_AI_GUIDE') {
          return getJolectAiGuideMarkdown(msg.jlAiGuideContent) || '';
        }
        return msg.textContent?.text || msg.imageWithTextContent?.text || '';
      });

      try {
        const transed = await (mode
          ? modeTranslate(texts, lang, mode, { isUseRedisCache: !translated })
          : overseaTranslate(texts, lang, 'chat', msgs[0].region));

        batchUpdateMessages(
          msgs.map((_msg, index) => {
            return [
              _msg.msgId,
              (msg) => {
                if (!msg) {
                  return;
                }
                const { msgId } = msg;
                const contentKey = getTranslationContentKey(msg.messageType);
                const text = transed[index];

                if (!msgId.startsWith('nim-')) {
                  translateReport(
                    {
                      msgId,
                      translation: {
                        text,
                        source,
                        targetLanguage: languageKey,
                      },
                    },
                    'Message',
                  );
                }

                return {
                  ...msg,
                  [contentKey]: {
                    ...(msg[contentKey] as any),
                    translationContent: {
                      ...msg[contentKey]?.translationContent,
                      [languageKey]: {
                        text,
                        source,
                        targetLanguage: languageKey,
                        status: 'success',
                        translated,
                        mode,
                      },
                    },
                  },
                };
              },
            ];
          }),
        );

        return transed;
      } catch (_e) {
        batchUpdateMessages(
          msgs.map((_msg) => {
            return [
              _msg.msgId,
              (msg) => {
                if (!msg) {
                  return;
                }
                const contentKey = getTranslationContentKey(msg.messageType);

                return {
                  ...msg,
                  [contentKey]: {
                    ...(msg[contentKey] as any),
                    translationContent: {
                      ...msg[contentKey]?.translationContent,
                      [languageKey]: {
                        language: languageKey,
                        status: 'error',
                        text: '',
                      },
                    },
                  },
                };
              },
            ];
          }),
        );

        throw _e;
      }
    },
  );

  return singleOrBatchTrans;
};
